import { clsx } from 'clsx'
import type { ReactNode } from 'react'

import type { TypeHorizontalAlignment, TypeVerticalAlignment } from '@contentful-types/alignment'

import {
  SingleColBeforeBreakpoints,
  TwoColumnGapSize,
  TwoColumnRatio,
  VerticalAlignment,
} from '@constants/enums/layout'

import { ImageContext } from '@contexts/image-context'

import type { LayoutColumn } from '@components/layout/types'
import { Column } from '@components/ui-core/column/column'
import type { FlexProps } from '@components/ui-core/types'

import styles from './two-column.module.scss'

interface TwoColumnLayoutProps {
  className?: string
  leftContent: ReactNode
  rightContent: ReactNode
  columnRatio: TwoColumnRatio
  gap?: TwoColumnGapSize
  reverseOrderWhenVertical?: boolean
  singleColBefore?: SingleColBeforeBreakpoints
  leftColumnVerticalAlignment?: VerticalAlignment
  rightColumnVerticalAlignment?: VerticalAlignment
}

// @TODO: Move this to it's own or utility file and add unit tests
// Possibly want to rethink how this works, maybe directly using
// `${left}fr ${right}fr`, instead of calculating the column width
// and setting grid-column.
const getColumnFromRatio = (ratio: TwoColumnRatio = TwoColumnRatio.OneToOne) => {
  const [leftStr, rightStr] = ratio.split(':')
  const left = Number(leftStr)
  const right = Number(rightStr)
  const totalCols = 12 / (left + right)
  return [(left * totalCols) as LayoutColumn, (right * totalCols) as LayoutColumn]
}

const gapToClassName: Record<NonNullable<TwoColumnLayoutProps['gap']>, string> = {
  None: styles.gapNone,
  M: styles.gapM,
  L: styles.gapL,
}

const crossAxisAlignment: Record<TypeHorizontalAlignment, FlexProps['crossAxisAlignment']> = {
  left: 'start',
  right: 'end',
  default: 'stretch',
  // TODO - this doesn't have an equivalent in the Column alignment types - should we remove it from CMS or add functionality?
  centerContentLeftAlignText: 'center',
}

const mainAxisAlignment: Record<TypeVerticalAlignment, FlexProps['mainAxisAlignment']> = {
  [VerticalAlignment.Top]: 'start',
  [VerticalAlignment.Bottom]: 'end',
  [VerticalAlignment.Default]: 'center',
}

export const TwoColumnLayout = ({
  className,
  leftContent,
  rightContent,
  columnRatio,
  gap = TwoColumnGapSize.None,
  reverseOrderWhenVertical,
  singleColBefore = SingleColBeforeBreakpoints.Small,
  leftColumnVerticalAlignment = VerticalAlignment.Default,
  rightColumnVerticalAlignment = VerticalAlignment.Default,
}: TwoColumnLayoutProps) => {
  const classes = clsx(
    styles.container,
    styles[`${singleColBefore}SingleCol`],
    gapToClassName[gap],
    {
      [styles.reversed]: reverseOrderWhenVertical,
    },
    className,
  )

  const [leftCols, rightCols] = getColumnFromRatio(columnRatio)

  const columns = [
    {
      className: 'leftColumn',
      verticalAlignment: leftColumnVerticalAlignment,
      horizontalAlignment: 'default' as TypeHorizontalAlignment,
      cols: leftCols,
      content: leftContent,
    },
    {
      className: 'rightColumn',
      verticalAlignment: rightColumnVerticalAlignment,
      horizontalAlignment: 'default' as TypeHorizontalAlignment,
      cols: rightCols,
      content: rightContent,
    },
  ]

  return (
    <div className={classes}>
      {columns?.length > 0 &&
        columns.map(
          (
            { className: columnClassName, verticalAlignment, horizontalAlignment, cols, content },
            index,
          ) => (
            <Column
              key={`${index}-${columnClassName}`}
              className={clsx(styles.column, styles[columnClassName])}
              style={{ '--column-grid-span': cols } as React.CSSProperties}
              mainAxisAlignment={mainAxisAlignment[verticalAlignment]}
              crossAxisAlignment={crossAxisAlignment[horizontalAlignment]}
            >
              <ImageContext.Provider value={{ cols }}>{content}</ImageContext.Provider>
            </Column>
          ),
        )}
    </div>
  )
}
